import type { PropertyHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyGroup/property_group.interface";
import { isSidebarSearching, matchesSidebarSearch } from "@verbleif/lib/src/components/Sidebar/sidebarSearch";
import { sort } from "fast-sort";

export interface PropertySidebarNode {
  routeId: string
  text: string
  children?: PropertySidebarNode[]
}

export interface PropertySidebarRow<T extends PropertySidebarNode> {
  id: string
  kind: "group" | "property"
  item: T
}

/**
 * Groups whose name matches keep all their objects; other groups keep only their matching objects
 * and drop out when none match.
 */
export function filterPropertySidebarGroups<T extends PropertySidebarNode>(
  groups: readonly T[],
  searchTerm: string,
): T[] {
  if (!isSidebarSearching(searchTerm)) {
    return [...groups];
  }

  return groups.flatMap((group) => {
    if (matchesSidebarSearch(group.text, searchTerm)) {
      return [group];
    }
    const children = (group.children ?? []).filter(child => matchesSidebarSearch(child.text, searchTerm));
    return children.length > 0 ? [{ ...group, children }] : [];
  });
}

export function buildPropertySidebarRows<T extends PropertySidebarNode>(
  groups: readonly T[],
  isOpen: (routeId: string) => boolean,
): PropertySidebarRow<T>[] {
  const rows: PropertySidebarRow<T>[] = [];

  for (const group of groups) {
    rows.push({ id: group.routeId, kind: "group", item: group });
    if (!isOpen(group.routeId)) {
      continue;
    }
    for (const child of group.children ?? []) {
      rows.push({ id: child.routeId, kind: "property", item: child as T });
    }
  }

  return rows;
}

export interface PropertyGroupEntry {
  groupId: number | null
  groupName: string
  properties: Array<{ id: number, name: string }>
}

/**
 * The active objects per object group, as Per object lists them: groups and objects by name with
 * the names the user prefers. Objects without a group are left out, as in the sidebar.
 */
export function groupActiveProperties(
  properties: readonly PropertyHydraItem[],
  groupsByIri: Record<string, PropertyGroupHydraItem>,
  propertyName: (property: PropertyHydraItem) => string,
  groupName: (group: PropertyGroupHydraItem) => string | null,
): PropertyGroupEntry[] {
  const named = sort(properties.filter(property => property.active).map((property) => {
    const group = groupsByIri[property.group];
    return {
      id: property.id,
      name: propertyName(property),
      groupName: group ? groupName(group) : null,
      groupId: group?.id ?? null,
    };
  })).asc([
    property => property.groupName ?? "",
    property => property.name,
  ]);

  const groups = new Map<string, PropertyGroupEntry>();
  for (const property of named) {
    if (!property.groupName) {
      continue;
    }
    let entry = groups.get(property.groupName);
    if (!entry) {
      entry = { groupId: property.groupId, groupName: property.groupName, properties: [] };
      groups.set(property.groupName, entry);
    }
    entry.properties.push({ id: property.id, name: property.name });
  }
  return [...groups.values()];
}
